// Budget API client. Thin wrapper over the global Axios instance in
// `frontend/src/api.js` — that instance already adds the X-User-Id header.
//
// Endpoints match design-note §6 verbatim. If the backend hasn't shipped a
// given route yet, callers get a 404; the UI is expected to surface an empty
// state, not crash.

import api from '../api';

const BASE = '/budget';

// Rebuild-then-read dashboard projection: `{projection_hash, ...payload}`.
export function fetchDashboard(fiscalYear) {
  return api.get(`${BASE}/dashboard/`, { params: { fiscal_year: fiscalYear } });
}

// One (line, FY, period|quarter) cell. Pass exactly one of period / quarter.
export function fetchDrilldown(budgetLineId, fiscalYear, { period, quarter } = {}) {
  const params = { budget_line_id: budgetLineId, fiscal_year: fiscalYear };
  if (period != null) params.period = period;
  if (quarter != null) params.quarter = quarter;
  return api.get(`${BASE}/drilldown/`, { params });
}

// Bucket drilldown: unassigned / needs_fx / fy_mismatch.
// Same response shape as fetchDrilldown — {sap_rows, invoices}.
export function fetchBucketDrilldown(bucket, fiscalYear) {
  return api.get(`${BASE}/drilldown/`, { params: { bucket, fiscal_year: fiscalYear } });
}

export function fetchLines(fiscalYear) {
  return api.get(`${BASE}/lines/`, { params: { fiscal_year: fiscalYear } });
}

export function createLine(body) {
  return api.post(`${BASE}/lines/`, body);
}

export function updateLine(lineId, body) {
  return api.patch(`${BASE}/lines/${lineId}/`, body);
}

export function fetchForecast(fiscalYear) {
  return api.get(`${BASE}/forecast/`, { params: { fiscal_year: fiscalYear } });
}

// Full-replace save: the payload is the complete grid for the FY — an active
// line omitted here gets zero budget in the new snapshot (design-note §6), so
// callers must always post every line they were given.
export function saveForecast(fiscalYear, snapshotReason, lines) {
  return api.post(`${BASE}/forecast/`, {
    fiscal_year: fiscalYear,
    snapshot_reason: snapshotReason,
    lines,
  });
}

export function fetchSnapshots(fiscalYear) {
  return api.get(`${BASE}/snapshots/`, { params: { fiscal_year: fiscalYear } });
}

// ── Actual to Budget tab ────────────────────────────────────────────────
// Rebuild-then-read projection re-keyed by PROJECT-FK. `{projection_hash,
// fiscal_year, snapshot, source_tab_all_heuristic, areas, totals, needs_fx,
// source_tabs, reconciling}`. `source_tab` is embedded for client-side
// filtering — all tabs ship in the single payload, so switching needs no
// refetch (design §3/§4). Pass `'all'` (the default) for the full grid.
export function fetchActualToBudget(fiscalYear, sourceTab = 'all') {
  return api.get(`${BASE}/actual-to-budget/`, {
    params: { fiscal_year: fiscalYear, source_tab: sourceTab },
  });
}

// Project-scoped drilldown for an Actual-to-Budget row — the SAP rows +
// committed invoices that make up the row's ACTUAL side. A2B actuals are
// keyed by the row's effective PROJECT-FK (not budget_line_id like the
// Dashboard), so pass exactly one of project_id (a project-keyed
// reconciled/budgeted row) or sap_transaction_id (a synthetic SAP-without-
// project row). Same {sap_rows, invoices} shape as fetchDrilldown — each row
// now also carries a per-row `project_name` (design §7.3).
export function fetchActualToBudgetDrilldown(fiscalYear, { projectId, sapTransactionId } = {}) {
  const params = { fiscal_year: fiscalYear };
  if (projectId != null) params.project_id = projectId;
  if (sapTransactionId != null) params.sap_transaction_id = sapTransactionId;
  return api.get(`${BASE}/actual-to-budget/drilldown/`, { params });
}

// Set / clear the MANUAL project attribution on a SAP row ("Update SAP
// Record"). `projectId` non-null pins the SAP row to an existing project
// (the manual override wins over reconciled derivation — design §4A); null
// clears it and the row reverts to its reconciled/synthetic project. The
// server validates the project exists and blocks split-batch members (B2).
export function setSapProject(sapTransactionId, projectId) {
  return api.post(`${BASE}/actual-to-budget/sap-project/${sapTransactionId}/`, {
    project_id: projectId,
  });
}

// Re-name a budgeted row for display (`display_alias`, NOT a join key) and/or
// set the row's PROJECT-FK (`project_id`, which DOES drive A2B matching —
// design §4E). exclude_unset semantics server-side: an omitted field is left
// unchanged, an explicit null clears that field. The project is validated
// (existing project only).
export function setLineAlias(lineId, { displayAlias, projectId } = {}) {
  const body = {};
  if (displayAlias !== undefined) body.display_alias = displayAlias;
  if (projectId !== undefined) body.project_id = projectId;
  return api.patch(`${BASE}/lines/${lineId}/alias/`, body);
}

// Existing-projects list for the typable project pickers (Update SAP Record +
// budget-line Rename). Reuses the invoice-context reference endpoint
// `GET /api/projects/` (ProjectResponse[]: {id, name, area, ...}); the
// combobox filters client-side as the user types.
export function fetchProjects() {
  return api.get('/projects');
}

// Subgroups are area-scoped + FY-scoped, with at-most-one-subgroup-per-row
// membership keyed by PROJECT-FK (`project_id` — design §5/§6).
export function fetchSubgroups(fiscalYear) {
  return api.get(`${BASE}/subgroups/`, { params: { fiscal_year: fiscalYear } });
}

export function createSubgroup(fiscalYear, area, name, sortOrder = 0) {
  return api.post(`${BASE}/subgroups/`, {
    fiscal_year: fiscalYear,
    area,
    name,
    sort_order: sortOrder,
  });
}

export function updateSubgroup(subgroupId, name) {
  return api.patch(`${BASE}/subgroups/${subgroupId}/`, { name });
}

export function deleteSubgroup(subgroupId) {
  return api.delete(`${BASE}/subgroups/${subgroupId}/`);
}

// Full-replace reorder: sort_order is derived from each id's position in
// `orderedIds` within the FY (drag-reorder of the subgroups themselves).
export function reorderSubgroups(fiscalYear, orderedIds) {
  return api.patch(`${BASE}/subgroups/`, {
    fiscal_year: fiscalYear,
    ordered_ids: orderedIds,
  });
}

// Only project-keyed rows are subgroupable — synthetic-area rows (no
// project_id) cannot be assigned (design §5). The server re-derives the
// project's area authoritatively, so the body carries only the project_id.
export function assignSubgroupMember(subgroupId, projectId) {
  return api.post(`${BASE}/subgroups/${subgroupId}/members/`, { project_id: projectId });
}

export function removeSubgroupMember(subgroupId, projectId) {
  return api.delete(`${BASE}/subgroups/${subgroupId}/members/`, {
    data: { project_id: projectId },
  });
}

// Rebuild-then-read slide projection: `{projection_hash, ...payload}`.
export function fetchSlidePage(fiscalYear) {
  return api.get(`${BASE}/slide/`, { params: { fiscal_year: fiscalYear } });
}

export function fetchSlideGroups(fiscalYear) {
  return api.get(`${BASE}/slide/groups/`, { params: { fiscal_year: fiscalYear } });
}

export function createSlideGroup(body) {
  return api.post(`${BASE}/slide/groups/`, body);
}

// `match_keys` is a full replace; `{comments: null}` clears the comment.
export function updateSlideGroup(groupId, body) {
  return api.patch(`${BASE}/slide/groups/${groupId}/`, body);
}

export function deleteSlideGroup(groupId) {
  return api.delete(`${BASE}/slide/groups/${groupId}/`);
}

// Multipart workbook upload. A failed grand reconciliation comes back as a
// 422 whose body is the SAME full-report shape as the 201 success payload
// (`imported: false`, `snapshot_id: null`) — ImportReportModal renders both
// and re-submits the same file with `force` / `expected_total`.
export function importWorkbook(file, fiscalYear, { expectedTotal, force } = {}) {
  const form = new FormData();
  form.append('file', file);
  form.append('fiscal_year', String(fiscalYear));
  if (expectedTotal != null && expectedTotal !== '') {
    form.append('expected_total', String(expectedTotal));
  }
  if (force) form.append('force', 'true');
  return api.post(`${BASE}/import/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// Export is a plain <a href> download (Content-Disposition: attachment
// server-side), not an axios call — design §7b.
export function exportWorkbookUrl(fiscalYear) {
  return `/api${BASE}/export/?fiscal_year=${fiscalYear}`;
}

export function fetchReviewQueue(fiscalYear, { threshold = 0.8 } = {}) {
  return api.get(`${BASE}/review/`, {
    params: { fiscal_year: fiscalYear, threshold },
  });
}

export function assignBudgetLine(sapTransactionId, budgetLineId) {
  return api.post(`${BASE}/review/${sapTransactionId}/assign/`, {
    budget_line_id: budgetLineId,
  });
}

// Queues the Celery classification batch (AI tier included) — 202.
export function runClassification(fiscalYear) {
  const body = {};
  if (fiscalYear != null) body.fiscal_year = fiscalYear;
  return api.post(`${BASE}/classify/run/`, body);
}
